import { FluentBundle, FluentResource } from '@fluent/bundle';
import { negotiateLanguages } from '@fluent/langneg';
import { LocalizationProvider, ReactLocalization } from '@fluent/react';
import React, { Children, FC, ReactNode, useEffect, useState } from 'react';

import en from './translations/en-US.ftl';
import it from './translations/it.ftl';

const DEFAULT_LOCALE = 'en-US';
const AVAILABLE_LOCALES = {
  'en-US': 'English',
  it: 'Italiano',
};

const ftl = {
  'en-US': en,
  it,
};

async function fetchMessages(locale: string): Promise<[string, string]> {
  const response = await fetch(ftl[locale]);
  const messages = await response.text();
  return [locale, messages];
}

function* lazilyParsedBundles(fetchedMessages: Array<[string, string]>) {
  // eslint-disable-next-line no-restricted-syntax
  for (const [locale, messages] of fetchedMessages) {
    const resource = new FluentResource(messages);
    const bundle = new FluentBundle(locale);
    bundle.addResource(resource);
    yield bundle;
  }
}

interface AppLocalizationProviderProps {
  children: ReactNode;
}

export const AppLocalizationProvider: FC = (props: AppLocalizationProviderProps) => {
  const [currentLocales, setCurrentLocales] = useState([DEFAULT_LOCALE]);
  const [l10n, setL10n] = useState<ReactLocalization | null>(null);

  async function changeLocales(userLocales: Array<string>) {
    const updatedLocales = negotiateLanguages(userLocales, Object.keys(AVAILABLE_LOCALES), {
      defaultLocale: DEFAULT_LOCALE,
    });
    setCurrentLocales(updatedLocales);

    const fetchedMessages = await Promise.all(updatedLocales.map(fetchMessages));

    const bundles = lazilyParsedBundles(fetchedMessages);
    setL10n(new ReactLocalization(bundles));
  }

  useEffect(() => {
    changeLocales(navigator.languages as Array<string>);
  }, []);

  if (l10n === null) {
    return <div>Loading…</div>;
  }

  return (
    <>
      <LocalizationProvider l10n={l10n}>{Children.only(props.children)}</LocalizationProvider>
      <select
        style={{
          position: 'fixed',
          bottom: '15px',
          right: '20px',
          zIndex: 1001,
        }}
        onChange={(event) => changeLocales([event.target.value])}
        value={currentLocales[0]}
      >
        {Object.entries(AVAILABLE_LOCALES).map(([code, name]) => (
          <option key={code} value={code}>
            {name}
          </option>
        ))}
      </select>
    </>
  );
};

export default { AppLocalizationProvider };
